import * as React from "react";

import type {
  AuthService,
  AuthSession,
  SignInResult,
  SignUpResult,
} from "@/core/auth/auth-service";

interface AuthContextValue {
  session: AuthSession | null;
  loading: boolean;
  signIn: AuthService["signIn"];
  confirmSignIn: AuthService["confirmSignIn"];
  signUp: AuthService["signUp"];
  confirmSignUp: AuthService["confirmSignUp"];
  resendSignUpCode: AuthService["resendSignUpCode"];
  resetPassword: AuthService["resetPassword"];
  confirmResetPassword: AuthService["confirmResetPassword"];
  signOut: () => Promise<void>;
  associatePasskey: AuthService["associatePasskey"];
  setMfa: AuthService["setMfa"];
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  authService: AuthService;
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  authService,
  children,
}) => {
  const [session, setSession] = React.useState<AuthSession | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async (): Promise<void> => {
    const next = await authService.currentSession();
    setSession(next);
  }, [authService]);

  React.useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const initial = await authService.currentSession();
        if (!cancelled) setSession(initial);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [authService]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      signIn: async (input): Promise<SignInResult> => {
        const result = await authService.signIn(input);
        if (result.isSignedIn) await refresh();
        return result;
      },
      confirmSignIn: async (resp): Promise<SignInResult> => {
        const result = await authService.confirmSignIn(resp);
        if (result.isSignedIn) await refresh();
        return result;
      },
      signUp: async (input): Promise<SignUpResult> => authService.signUp(input),
      confirmSignUp: (input): Promise<void> => authService.confirmSignUp(input),
      resendSignUpCode: (email): Promise<void> =>
        authService.resendSignUpCode(email),
      resetPassword: (email): Promise<void> => authService.resetPassword(email),
      confirmResetPassword: (input): Promise<void> =>
        authService.confirmResetPassword(input),
      signOut: async (): Promise<void> => {
        await authService.signOut();
        setSession(null);
      },
      associatePasskey: (): Promise<void> => authService.associatePasskey(),
      setMfa: (enabled): Promise<{ setupUri: string | null }> =>
        authService.setMfa(enabled),
      refresh,
    }),
    [authService, session, loading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
};
